import {AmountBucket, ReportRowKind, type ViewRow} from "../../types";
import type {SbsSection} from "../../../../../generated/enums.ts";

export const IMBALANCED_BALANCE_CHECK_ROW =
    "bg-[#FCEBEB] font-bold text-gray-900 hover:bg-[#F9DDDD] [&>*]:[border-top:1px_solid_#fecaca] [&>*]:[border-bottom:1px_solid_#fecaca]";
export const IMBALANCED_BALANCE_CHECK_STICKY_CELL = "bg-[#FCEBEB] group-hover:bg-[#F9DDDD]";

export const COL_SEP_L = "border-l border-l-gray-300";
export const COL_SEP_R = "border-r border-r-gray-300";

export const COLOR_POSITIVE = "text-green-600";
export const COLOR_NEGATIVE = "text-red-600";

export interface AmountGroup {
    label: string;
    bucket: AmountBucket;
}

export const AMOUNT_GROUPS: AmountGroup[] = [
    {label: "Proefbalans", bucket: AmountBucket.TrialBalance},
    {label: "Saldibalans", bucket: AmountBucket.NetTrialBalance},
    {label: "Verlies & Winst", bucket: AmountBucket.ProfitAndLoss},
    {label: "Balans", bucket: AmountBucket.BalanceSheet},
];

export const SLIM_TABLE_BASE_WIDTH = 420;
export const SLIM_TABLE_COMPARISON_EXTRA_WIDTH = 240;
export const CLASSIC_TABLE_BASE_WIDTH = 320;
export const CLASSIC_TABLE_GROUP_COLUMN_WIDTH = 220;
export const CLASSIC_TABLE_COMPARISON_EXTRA_WIDTH = 320;

export function statementTableMinWidth(
    isSlim: boolean,
    groupCount: number,
    hasComparison: boolean,
): number {
    if (isSlim) {
        return SLIM_TABLE_BASE_WIDTH + (hasComparison ? SLIM_TABLE_COMPARISON_EXTRA_WIDTH : 0);
    }
    return (
        CLASSIC_TABLE_BASE_WIDTH +
        groupCount * CLASSIC_TABLE_GROUP_COLUMN_WIDTH +
        (hasComparison ? CLASSIC_TABLE_COMPARISON_EXTRA_WIDTH : 0)
    );
}

export enum BadgeVariant {
    Kpi = "kpi",
    Subtotal = "subtotal",
}

export function signColorClass(cents: number): string {
    if (cents > 0) return COLOR_POSITIVE;
    if (cents < 0) return COLOR_NEGATIVE;
    return "text-gray-400";
}

export function isBalanceCheckImbalanced(row: ViewRow, balanceDiffCents: number): boolean {
    return row.kind === ReportRowKind.BalanceCheck && balanceDiffCents !== 0;
}

export function isBucketBlankForSection(
    sectionKind: SbsSection,
    bucket: AmountBucket,
): boolean {
    if (sectionKind === 'balance_sheet' && bucket === AmountBucket.ProfitAndLoss) {
        return true;
    }
    if (sectionKind === 'profit_and_loss' && bucket === AmountBucket.BalanceSheet) {
        return true;
    }
    return false;
}

export function isBlankedHeader(row: ViewRow): boolean {
    return row.kind === ReportRowKind.Group && row.expanded;
}

export function levelPadding(level: number): string {
    if (level <= 1) return "pl-3";
    if (level === 2) return "pl-8";
    return "pl-12";
}

export function rowClassName(row: ViewRow, isImbalanced: boolean): string {
    switch (row.kind) {
        case ReportRowKind.Group:
            return "bg-white font-medium text-gray-900 [&>*]:border-b [&>*]:border-b-gray-100";
        case ReportRowKind.Subtotal:
            return "bg-gray-50 font-bold text-gray-800 [&>*]:[border-bottom:1px_dashed_#d1d5db]";
        case ReportRowKind.Kpi:
            return "bg-[#F4FAFE] font-bold text-gray-900 hover:bg-[#EAF5FD] [&>*]:[border-top:1px_solid_#d1d5db] [&>*]:[border-bottom:1px_solid_#d1d5db]";
        case ReportRowKind.BalanceCheck:
            return isImbalanced
                ? IMBALANCED_BALANCE_CHECK_ROW
                : "bg-[#F4FAFE] font-bold text-gray-900 hover:bg-[#EAF5FD] [&>*]:[border-top:1px_solid_#d1d5db] [&>*]:[border-bottom:1px_solid_#d1d5db]";
        case ReportRowKind.Account:
            return "bg-white hover:bg-gray-50 text-gray-700 [&>*]:border-b [&>*]:border-b-gray-100";
        default: {
            const _exhaustive: never = row.kind;
            return _exhaustive;
        }
    }
}

export function stickyAccountCellBg(row: ViewRow, isImbalanced: boolean): string {
    switch (row.kind) {
        case ReportRowKind.Group:
            return "bg-white";
        case ReportRowKind.Subtotal:
            return "bg-gray-50";
        case ReportRowKind.Kpi:
            return "bg-[#F4FAFE] group-hover:bg-[#EAF5FD]";
        case ReportRowKind.BalanceCheck:
            return isImbalanced
                ? IMBALANCED_BALANCE_CHECK_STICKY_CELL
                : "bg-[#F4FAFE] group-hover:bg-[#EAF5FD]";
        case ReportRowKind.Account:
            return "bg-white group-hover:bg-gray-50";
        default: {
            const _exhaustive: never = row.kind;
            return _exhaustive;
        }
    }
}
